import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Chip, Stack, IconButton, Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StatusChip from "./_StatusChip";

/**
 * Per-row detail dialog for booking logs (Module G). Mirrors
 * LogDetailDialog.js for the Insights side, but the booking log
 * shape includes extra fields (transcript, clarification_turns,
 * final_json, user_edited_fields) that the Insights view doesn't.
 */
export default function BookingLogDetailDialog({ row, onClose }) {
  if (!row) return null;

  const copy = (data) => {
    try {
      navigator.clipboard.writeText(typeof data === "string" ? data : JSON.stringify(data, null, 2));
    } catch (_) { /* ignore */ }
  };

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          borderLeft: "5px solid",
          borderImage: "linear-gradient(180deg, #06B6D4, #0891B2) 1",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #ECFEFF, #E0F2FE)",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: "#0F172A" }}>
              Booking Log #{row.id}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
              {new Date(row.createdAt).toLocaleString()} · user {row.userId ?? "(anon)"} · {row.model ?? "—"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusChip status={row.status} />
            {row.isAudio && (
              <Chip
                size="small"
                label="🎤 audio"
                sx={{
                  bgcolor: "#FCE7F3",
                  color: "#9D174D",
                  fontWeight: 700,
                  fontSize: 10,
                  height: 22,
                }}
              />
            )}
            {row.confidence != null && (
              <Chip
                size="small"
                variant="outlined"
                label={`conf ${row.confidence.toFixed(2)}`}
                color={row.confidence < 0.6 ? "warning" : "default"}
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Section title="Request text" onCopy={() => copy(row.requestText)}>
          <Typography sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
            {row.requestText || "(empty)"}
          </Typography>
        </Section>

        {row.transcript && (
          <Section title="Audio transcript (verbatim from Gemini)" onCopy={() => copy(row.transcript)}>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {row.transcript}
            </Typography>
          </Section>
        )}

        <Section title="Initial extraction (LLM output)" onCopy={() => copy(row.extracted)}>
          <Pre>{JSON.stringify(row.extracted || null, null, 2)}</Pre>
        </Section>

        {Array.isArray(row.clarificationTurns) && row.clarificationTurns.length > 0 && (
          <Section title={`Clarification turns (${row.clarificationTurns.length})`} onCopy={() => copy(row.clarificationTurns)}>
            <Pre>{JSON.stringify(row.clarificationTurns, null, 2)}</Pre>
          </Section>
        )}

        {row.finalJson && (
          <Section title="Final JSON (after clarification + edits)" onCopy={() => copy(row.finalJson)}>
            <Pre>{JSON.stringify(row.finalJson, null, 2)}</Pre>
          </Section>
        )}

        {row.userEditedFields && (
          <Section title="User-edited fields" onCopy={() => copy(row.userEditedFields)}>
            <Pre>{JSON.stringify(row.userEditedFields, null, 2)}</Pre>
          </Section>
        )}

        {row.errorMessage && (
          <Section title="Error">
            <Typography color="error" sx={{ fontFamily: "monospace" }}>{row.errorMessage}</Typography>
          </Section>
        )}

        <Section title="Tokens / latency">
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Field label="Input tokens"  value={row.tokens?.in} />
            <Field label="Output tokens" value={row.tokens?.out} />
            <Field label="Total tokens"  value={row.tokens?.total} />
            <Field label="Latency (ms)"  value={row.latencyMs} />
            <Field label="Model"         value={row.model || "—"} mono />
          </Stack>
        </Section>

        {row.isAudio && row.audio && (
          <Section title="Audio">
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Field label="Duration (s)" value={row.audio.durationSec} />
              <Field label="MIME"         value={row.audio.mimeType} mono />
              <Field label="Bytes"        value={row.audio.bytes?.toLocaleString()} />
            </Stack>
          </Section>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Section({ title, children, onCopy }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: 11,
          }}
        >
          {title}
        </Typography>
        {onCopy && (
          <IconButton size="small" onClick={onCopy} title="Copy">
            <ContentCopyIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
          </IconButton>
        )}
      </Stack>
      {children}
      <Divider sx={{ mt: 1.25, borderColor: "#F1F5F9" }} />
    </Box>
  );
}

function Field({ label, value, mono }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontFamily: mono ? "monospace" : undefined, fontWeight: 700, color: "#0F172A" }}>
        {value == null || value === "" ? "—" : value}
      </Typography>
    </Box>
  );
}

function Pre({ children }) {
  return (
    <Box
      component="pre"
      sx={{
        bgcolor: "#0F172A",
        color: "#E2E8F0",
        borderRadius: 1.5,
        p: 1.5,
        fontSize: 11,
        overflow: "auto",
        maxHeight: 280,
        m: 0,
        fontFamily: '"Fira Code", "SF Mono", monospace',
        lineHeight: 1.5,
      }}
    >
      {children}
    </Box>
  );
}
